/**
 * L'ONGLET EXÉCUTION D'UN ORDRE — lot 161, 04/11/2026 (eCMR, bêta).
 *
 * Validé dans la démo « Démo eCMR » (vue Exécution, option A, 04/11) et
 * redécoupé le même jour : ce lot ne porte que la carte « Lettres de
 * voiture » — générer, voir, refaire. LOT 162 : la carte « Liens », avec le
 * lien de suivi CLIENT (créer, copier, ouvrir, refaire, couper). Le lien
 * chauffeur et les étapes signées arrivent au lot 163.
 *
 * Bêta EN PARALLÈLE DE GEDMOUV : la lettre Stafa n'a pas encore valeur de
 * document officiel (décision du 04/11).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { EchecApi } from './api.js';
import { genererLesLettres, lireLesLettres, refaireLaLettre } from './apiLettresVoiture.js';
import {
  adresseDeLaMission,
  adresseDuSuivi,
  chauffeursProposes,
  couperLeLien,
  creerLeLienChauffeur,
  creerLeLienClient,
  lireLExecution,
  lireLesLiens,
  etatDeLaLettre,
  executionDeLaLettre,
  toutRecommencer,
  type ChoixChauffeur,
  type EtatDesLiens,
  type ExecutionLue,
  type LienSuivi,
} from './apiLiensSuivi.js';
import { momentDe } from './suivi.js';
import {
  dateDite,
  ouvrirLePdfDeLettre,
  remplirLettre,
  reserveDite,
  type EtatDesLettres,
  type ExecutionDeLettre,
  type LettreVoiture,
} from './lettreVoiture.js';

/** La lettre ouverte, à l'échelle de la fenêtre. */
function ApercuLettre({
  lettre,
  ex,
  quandFermer,
}: {
  readonly lettre: LettreVoiture;
  readonly ex: ExecutionDeLettre | null;
  readonly quandFermer: () => void;
}) {
  const cadre = useRef<HTMLDivElement>(null);
  const [echelle, setEchelle] = useState(1);
  useEffect(() => {
    const ajuster = () => {
      const largeur = cadre.current?.clientWidth ?? 800;
      // 595 pt = 793 px : la page A4 du modèle.
      setEchelle(Math.min(1.3, largeur / 793));
    };
    ajuster();
    window.addEventListener('resize', ajuster);
    return () => window.removeEventListener('resize', ajuster);
  }, []);
  return (
    <div className="voile-boite" role="presentation" onClick={quandFermer}>
      <div
        className="boite-lettre-voiture"
        role="dialog"
        aria-modal="true"
        aria-label={`Lettre de voiture ${lettre.numero}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tete-lettre-voiture">
          <strong>Lettre de voiture {lettre.numero}</strong>
          <button className="bouton bouton-discret" type="button" onClick={quandFermer}>
            Fermer
          </button>
        </div>
        <div className="cadre-lettre-voiture" ref={cadre}>
          <div
            className="page-lettre-voiture"
            style={{ transform: `scale(${String(echelle)})`, height: `${String(1123 * echelle)}px` }}
            // Le modèle figé, rempli par `remplirLettre`, qui échappe chaque
            // valeur saisie. Rien d'autre n'entre ici.
            dangerouslySetInnerHTML={{ __html: remplirLettre(lettre, ex) }}
          />
        </div>
      </div>
    </div>
  );
}

/** Une étape de l'ordre, telle que la fiche la connaît. */
export interface EtapeDeLaFiche {
  readonly id: string;
  readonly type: string;
  readonly site_nom: string;
  readonly site_ville: string | null;
  readonly jour: string;
  readonly heure_debut: string | null;
  readonly heure_fin: string | null;
  /** LOT 168 — le pointage, revenu dans l'Exécution. */
  readonly fait?: boolean;
  readonly echange_support?: string | null;
  readonly litige_id?: string | null;
  readonly motif_litige_id?: string | null;
  readonly motif_litige?: string | null;
  readonly litige_note?: string | null;
  readonly litige_resolu_le?: string | null;
}

/**
 * LE POINTAGE DE L'EXPLOITATION — lot 168 (05/11) : valider l'étape, le
 * litige et l'échange d'emballage, sous chaque étape de l'Exécution (ils
 * étaient sur l'ancien onglet « Étapes »).
 */
export interface Pointage {
  /** L'ordre est parti en facturation : valider ne se propose plus. */
  readonly gelee: boolean;
  readonly occupee: boolean;
  readonly quandBasculerFait: (etapeId: string) => void;
  readonly quandLitige: (etapeId: string) => void;
  readonly quandResoudre: (litigeId: string) => void;
  readonly quandEchange: (etapeId: string) => void;
}

type ArretFait = ExecutionLue['etapes'][number];

function BlocEtape(p: {
  readonly etape: EtapeDeLaFiche;
  readonly f: ArretFait | null;
  /** Une lettre couvre l'étape : les signatures et la réserve se montrent. */
  readonly signee: boolean;
  readonly chauffeur: string | null;
  readonly pointage: Pointage | null;
}) {
  const { etape: e, f } = p;
  const charge = e.type === 'chargement';
  const faite = e.fait === true || (f?.parti_le ?? null) !== null;
  const signe = (f?.signe_le ?? null) !== null;
  const po = p.pointage;
  return (
    <div className="ex-etape">
      <div className="ex-etape-tete">
        <span className={faite ? 'ex-point ex-point-plein' : 'ex-point'} />
        {charge ? 'Chargement' : 'Livraison'} — {e.site_nom}
        {e.fait === true && <span className="ex-pastille ex-vert">Validée</span>}
      </div>
      <div className="ex-heures">
        <span>
          <span className="ex-sourd">Arrivée</span> <b>{momentDe(f?.arrive_le)}</b>
        </span>
        <span>
          <span className="ex-sourd">Départ</span> <b>{momentDe(f?.parti_le)}</b>
        </span>
      </div>
      {p.signee && (
        <>
          <div className="ex-signe">
            {signe ? <span className="ex-coche">✓</span> : <span className="ex-point" />}
            <span>{charge ? 'Expéditeur' : 'Destinataire'}</span>
            {signe ? (
              <>
                <b>{f?.signataire ?? 'Signé'}</b>
                <span className="ex-sourd">{momentDe(f?.signe_le)}</span>
              </>
            ) : (
              <span className="ex-attente">en attente</span>
            )}
          </div>
          <div className="ex-signe">
            {signe ? <span className="ex-coche">✓</span> : <span className="ex-point" />}
            <span>Transporteur</span>
            {signe ? (
              <>
                <b>{p.chauffeur ?? 'Chauffeur'}</b>
                <span className="ex-sourd">{momentDe(f?.signe_le)}</span>
              </>
            ) : (
              <span className="ex-attente">en attente</span>
            )}
          </div>
          <div>
            <span className="ex-sourd">Réserve</span>{' '}
            {signe ? reserveDite(f, charge ? 'Expéditeur' : 'Destinataire') || '—' : '—'}
          </div>
        </>
      )}
      {(e.motif_litige_id ?? null) !== null && (
        <div className="ex-litige">
          <b>{e.motif_litige ?? 'Litige'}</b>
          {(e.litige_note ?? null) !== null && <span>{e.litige_note}</span>}
          {po !== null && (e.litige_id ?? null) !== null && (e.litige_resolu_le ?? null) === null && (
            <button className="ex-lien" type="button" disabled={po.occupee} onClick={() => po.quandResoudre(e.litige_id as string)}>
              Résoudre
            </button>
          )}
        </div>
      )}
      {po !== null && (
        <div className="ex-actions-etape">
          {!po.gelee && (
            <button className="ex-lien" type="button" disabled={po.occupee} onClick={() => po.quandBasculerFait(e.id)}>
              {e.fait === true ? 'Dévalider l’étape' : 'Valider l’étape'}
            </button>
          )}
          {(e.motif_litige_id ?? null) === null && (
            <button className="ex-lien" type="button" disabled={po.occupee} onClick={() => po.quandLitige(e.id)}>
              Litige
            </button>
          )}
          <button className="ex-lien" type="button" disabled={po.occupee} onClick={() => po.quandEchange(e.id)}>
            {(e.echange_support ?? null) === null ? 'Échange d’emballage' : 'Échange d’emballage ✓'}
          </button>
        </div>
      )}
    </div>
  );
}

/** Un lien de la carte « Liens » : l'adresse, Copier, Couper. */
function BlocLien(p: {
  readonly titre: string;
  readonly qui: string | null;
  readonly adresse: string;
  readonly copie: boolean;
  readonly envoi: boolean;
  readonly quandCopier: () => void;
  readonly quandCouper: () => void;
}) {
  return (
    <div className="ex-bloc">
      <div className="ex-bloc-tete">
        <b>{p.titre}</b>
        <span className="ex-pastille ex-vert">Actif</span>
        {p.qui !== null && <span className="ex-sourd ex-droite">{p.qui}</span>}
      </div>
      <div className="ex-ligne-lien">
        <input className="ex-champ" readOnly value={p.adresse} aria-label={p.titre} onFocus={(x) => x.target.select()} />
        <button className="ex-btn" type="button" onClick={p.quandCopier}>
          {p.copie ? 'Copié' : 'Copier'}
        </button>
        <button className="ex-btn" type="button" disabled={p.envoi} onClick={p.quandCouper}>
          Couper
        </button>
      </div>
    </div>
  );
}

export function VueExecution(p: {
  readonly etat: EtatDesLettres | null;
  readonly refus: string | null;
  readonly envoi: boolean;
  readonly ouverte: LettreVoiture | null;
  readonly quandGenerer: () => void;
  readonly quandRefaire: (l: LettreVoiture) => void;
  readonly quandVoir: (l: LettreVoiture | null) => void;
  /** LOT 162 — la carte « Liens ». `null` : pas encore lus. */
  readonly liens: readonly LienSuivi[] | null;
  readonly origine: string;
  readonly refusLiens: string | null;
  readonly copie: boolean;
  readonly quandCreerLien: () => void;
  readonly quandCopierLien: (adresse: string) => void;
  readonly quandRefaireLien: (l: LienSuivi) => void;
  readonly quandCouperLien: (l: LienSuivi) => void;
  /** LOT 163 — le lien chauffeur et ce que le chauffeur a fait. */
  readonly copieChauffeur: boolean;
  readonly quandCreerLienChauffeur: (chauffeurId: string) => void;
  /** LOT 165 — le chauffeur se choisit dans la liste ; « Tout recommencer ». */
  readonly choix: ChoixChauffeur | null;
  readonly quandRecommencer: () => void;
  readonly quandCopierLienChauffeur: (adresse: string) => void;
  readonly etapes: readonly EtapeDeLaFiche[];
  readonly execution: ExecutionLue | null;
  /** LOT 168 — le pointage ; absent dans les aperçus. */
  readonly pointage?: Pointage | null;
}) {
  const e = p.etat;
  const client = p.liens?.find((l) => l.genre === 'client') ?? null;
  const chauffeur = p.liens?.find((l) => l.genre === 'chauffeur') ?? null;
  const proposes = p.choix === null ? null : chauffeursProposes(p.choix);
  const [choisi, setChoisi] = useState('');
  // Le chauffeur du planning, seul proposé, est déjà choisi.
  const chauffeurId = proposes?.liste.length === 1 ? (proposes.liste[0]?.id ?? '') : choisi;
  const [confirmer, setConfirmer] = useState(false);
  const faites = new Map((p.execution?.etapes ?? []).map((x) => [x.etape_id, x]));
  const pointage = p.pointage ?? null;
  const lettres = e?.lettres ?? [];
  const couvertes = new Set(lettres.flatMap((l) => [l.etape_chargement_id, l.etape_livraison_id]));
  const libres = p.etapes.filter((x) => !couvertes.has(x.id));
  const etapeDe = (id: string) => p.etapes.find((x) => x.id === id) ?? null;
  const plaques = p.execution?.plaques ?? null;
  const ligneRessource = [
    p.execution?.chauffeur ?? null,
    plaques === null ? null : `tracteur ${plaques.tracteur}`,
    plaques === null ? null : plaques.remorque === null ? 'sans remorque' : `semi ${plaques.remorque}`,
  ].filter((x) => x !== null);
  return (
    <div className="onglet-execution ex">
      {ligneRessource.length > 0 && <span className="ex-sourd">{ligneRessource.join(' · ')}</span>}
      {p.refus !== null && <p className="bandeau bandeau-refus">{p.refus}</p>}

      <section className="ex-carte">
        <b className="ex-titre">Liens</b>
        {p.refusLiens !== null && <p className="bandeau bandeau-refus">{p.refusLiens}</p>}
        {p.liens === null ? (
          p.refusLiens === null && <p className="attente">Chargement…</p>
        ) : (
          <div className="ex-deux">
            {client === null ? (
              <div className="ex-bloc">
                <div className="ex-bloc-tete">
                  <b>Suivi client</b>
                </div>
                <div className="ex-ligne-lien">
                  <button className="ex-btn ex-btn-p" type="button" disabled={p.envoi} onClick={p.quandCreerLien}>
                    Créer le lien
                  </button>
                </div>
              </div>
            ) : (
              <BlocLien
                titre="Suivi client"
                qui={null}
                adresse={adresseDuSuivi(p.origine, client.code)}
                copie={p.copie}
                envoi={p.envoi}
                quandCopier={() => p.quandCopierLien(adresseDuSuivi(p.origine, client.code))}
                quandCouper={() => p.quandCouperLien(client)}
              />
            )}
            {chauffeur === null ? (
              <form
                className="ex-bloc"
                onSubmit={(x) => {
                  x.preventDefault();
                  if (chauffeurId !== '') p.quandCreerLienChauffeur(chauffeurId);
                }}
              >
                <div className="ex-bloc-tete">
                  <b>Chauffeur</b>
                </div>
                {proposes?.bloque != null ? (
                  <span className="ex-sourd">{proposes.bloque}</span>
                ) : (
                  <div className="ex-ligne-lien">
                    <select
                      className="ex-champ"
                      aria-label="Chauffeur"
                      value={chauffeurId}
                      disabled={proposes === null || proposes.liste.length === 1}
                      onChange={(x) => setChoisi(x.target.value)}
                    >
                      {proposes !== null && proposes.liste.length !== 1 && <option value="">Choisir le chauffeur</option>}
                      {(proposes?.liste ?? []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nom}
                        </option>
                      ))}
                    </select>
                    <button className="ex-btn ex-btn-p" type="submit" disabled={p.envoi || chauffeurId === ''}>
                      Créer le lien
                    </button>
                  </div>
                )}
              </form>
            ) : (
              <BlocLien
                titre="Chauffeur"
                qui={chauffeur.chauffeur_nom}
                adresse={adresseDeLaMission(p.origine, chauffeur.code)}
                copie={p.copieChauffeur}
                envoi={p.envoi}
                quandCopier={() => p.quandCopierLienChauffeur(adresseDeLaMission(p.origine, chauffeur.code))}
                quandCouper={() => p.quandCouperLien(chauffeur)}
              />
            )}
          </div>
        )}
      </section>

      {e === null && p.refus === null && <p className="attente">Chargement…</p>}

      {lettres.map((l) => {
        const ex = executionDeLaLettre(p.execution, l);
        // LOT 164 : un chargement signé fige la lettre.
        const signee = (ex?.chargement?.signe_le ?? null) !== null;
        const etat = etatDeLaLettre(l, ex);
        const ch = etapeDe(l.etape_chargement_id);
        const li = etapeDe(l.etape_livraison_id);
        return (
          <section className="ex-carte" key={l.id}>
            <div className="ex-carte-tete">
              <b className="ex-titre">
                {l.contenu.expediteur.nom ?? '—'} → {l.contenu.destinataire.nom ?? '—'}
              </b>
              <span className="ex-pastille ex-bleu">eCMR</span>
              <span className={etat.startsWith('Générée') ? 'ex-pastille ex-gris' : etat.startsWith('En route') ? 'ex-pastille ex-ambre' : 'ex-pastille ex-vert'}>
                {etat}
              </span>
              <span className="ex-droite ex-outils">
                <span className="ex-sourd">
                  N° {l.numero} · générée le {dateDite(l.generee_le)}
                </span>
                <button className="ex-btn" type="button" onClick={() => p.quandVoir(l)}>
                  Voir la lettre de voiture
                </button>
                <button
                  className="ex-btn"
                  type="button"
                  disabled={p.envoi || signee}
                  title={signee ? 'Déjà signée' : undefined}
                  onClick={() => p.quandRefaire(l)}
                >
                  Refaire
                </button>
              </span>
            </div>
            <div className="ex-deux">
              {[ch, li].map((x) =>
                x === null ? null : (
                  <BlocEtape
                    key={x.id}
                    etape={x}
                    f={faites.get(x.id) ?? null}
                    signee
                    chauffeur={p.execution?.chauffeur ?? null}
                    pointage={pointage}
                  />
                ),
              )}
            </div>
          </section>
        );
      })}

      {e !== null && (libres.length > 0 || e.couples_sans_lettre > 0) && (
        <section className="ex-carte">
          <div className="ex-carte-tete">
            <span className="ex-pastille ex-gris">
              {e.couples === 0 ? 'Aucune marchandise dans le Packaging' : 'Pas encore de lettre de voiture'}
            </span>
            {e.couples_sans_lettre > 0 && (
              <span className="ex-droite ex-outils">
                <button className="ex-btn ex-btn-p" type="button" disabled={p.envoi} onClick={p.quandGenerer}>
                  Générer la lettre de voiture
                </button>
              </span>
            )}
          </div>
          {libres.length > 0 && (
            <div className="ex-deux">
              {libres.map((x) => (
                <BlocEtape
                  key={x.id}
                  etape={x}
                  f={faites.get(x.id) ?? null}
                  signee={false}
                  chauffeur={null}
                  pointage={pointage}
                />
              ))}
            </div>
          )}
        </section>
      )}

      <div className="ex-pied">
        {confirmer ? (
          <div className="ex-confirmer">
            <span>Tout effacer : statuts, signatures, lettres et liens.</span>
            <button
              className="ex-btn ex-btn-risque"
              type="button"
              disabled={p.envoi}
              onClick={() => {
                setConfirmer(false);
                p.quandRecommencer();
              }}
            >
              Confirmer
            </button>
            <button className="ex-btn" type="button" onClick={() => setConfirmer(false)}>
              Ne rien faire
            </button>
          </div>
        ) : (
          <button className="ex-btn ex-btn-risque" type="button" disabled={p.envoi} onClick={() => setConfirmer(true)}>
            Tout recommencer
          </button>
        )}
      </div>
      {p.ouverte !== null && (
        <ApercuLettre
          lettre={p.ouverte}
          ex={executionDeLaLettre(p.execution, p.ouverte)}
          quandFermer={() => p.quandVoir(null)}
        />
      )}
    </div>
  );
}

export function OngletExecution({
  jeton,
  otId,
  etapes,
  quandAccesPerdu,
  pointage = null,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly otId: string;
  readonly etapes: readonly EtapeDeLaFiche[];
  readonly quandAccesPerdu: () => void;
  readonly pointage?: Pointage | null;
}) {
  const [etat, setEtat] = useState<EtatDesLettres | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [ouverte, setOuverte] = useState<LettreVoiture | null>(null);
  const [liens, setLiens] = useState<readonly LienSuivi[] | null>(null);
  const [choix, setChoix] = useState<ChoixChauffeur | null>(null);
  const poserLesLiens = (e: EtatDesLiens) => {
    setLiens(e.liens);
    setChoix(e.choix);
  };
  const [refusLiens, setRefusLiens] = useState<string | null>(null);
  const [copie, setCopie] = useState(false);
  const [copieChauffeur, setCopieChauffeur] = useState(false);
  const [execution, setExecution] = useState<ExecutionLue | null>(null);

  const traiter = useCallback(
    (r: { etat: 'ok'; valeur: EtatDesLettres } | { etat: 'echec'; cause: EchecApi; message?: string }, defaut: string) => {
      if (r.etat === 'ok') {
        setEtat(r.valeur);
        setRefus(null);
        return;
      }
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(r.message ?? defaut);
    },
    [quandAccesPerdu],
  );

  useEffect(() => {
    let vivant = true;
    void (async () => {
      const j = await jeton();
      if (j === null) {
        quandAccesPerdu();
        return;
      }
      const r = await lireLesLettres(j, otId);
      if (vivant) traiter(r, 'Les lettres de voiture n’ont pas pu être lues.');
      const l = await lireLesLiens(j, otId);
      if (!vivant) return;
      if (l.etat === 'ok') poserLesLiens(l.valeur);
      else setRefusLiens(l.message ?? 'Les liens n’ont pas pu être lus.');
      const x = await lireLExecution(j, otId);
      if (vivant && x.etat === 'ok') setExecution(x.valeur);
    })();
    return () => {
      vivant = false;
    };
  }, [jeton, otId, quandAccesPerdu, traiter]);

  const agir = (appel: (j: string) => ReturnType<typeof lireLesLettres>, defaut: string) => {
    setEnvoi(true);
    void (async () => {
      const j = await jeton();
      if (j === null) {
        setEnvoi(false);
        quandAccesPerdu();
        return;
      }
      const r = await appel(j);
      setEnvoi(false);
      traiter(r, defaut);
    })();
  };

  /** Une suite d'appels sur les liens ; le dernier résultat devient la liste. */
  const agirSurLesLiens = (
    appels: readonly ((j: string) => Promise<Awaited<ReturnType<typeof lireLesLiens>>>)[],
    defaut: string,
  ) => {
    setEnvoi(true);
    setCopie(false);
    setCopieChauffeur(false);
    void (async () => {
      const j = await jeton();
      if (j === null) {
        setEnvoi(false);
        quandAccesPerdu();
        return;
      }
      for (const appel of appels) {
        const r = await appel(j);
        if (r.etat !== 'ok') {
          setEnvoi(false);
          if (r.cause === 'acces') quandAccesPerdu();
          setRefusLiens(r.message ?? defaut);
          return;
        }
        poserLesLiens(r.valeur);
      }
      setEnvoi(false);
      setRefusLiens(null);
    })();
  };

  return (
    <VueExecution
      etat={etat}
      refus={refus}
      envoi={envoi}
      ouverte={ouverte}
      quandGenerer={() => agir((j) => genererLesLettres(j, otId), 'Les lettres de voiture n’ont pas pu être générées.')}
      quandRefaire={(l) =>
        agir((j) => refaireLaLettre(j, otId, l.id), 'La lettre de voiture n’a pas pu être refaite.')
      }
      quandVoir={setOuverte}
      liens={liens}
      origine={window.location.origin}
      refusLiens={refusLiens}
      copie={copie}
      quandCreerLien={() => agirSurLesLiens([(j) => creerLeLienClient(j, otId)], 'Le lien n’a pas pu être créé.')}
      quandCopierLien={(adresse) => {
        void navigator.clipboard.writeText(adresse).then(
          () => setCopie(true),
          () => setRefusLiens('La copie a été refusée par le navigateur : sélectionnez l’adresse à la main.'),
        );
      }}
      quandRefaireLien={(l) =>
        agirSurLesLiens(
          [
            (j) => couperLeLien(j, otId, l.id),
            (j) =>
              // Un lien chauffeur d'avant le 05/11 n'a pas d'identifiant : il
              // est seulement coupé, le chauffeur se choisit ensuite.
              l.genre === 'chauffeur'
                ? l.chauffeur_id === null
                  ? lireLesLiens(j, otId)
                  : creerLeLienChauffeur(j, otId, l.chauffeur_id)
                : creerLeLienClient(j, otId),
          ],
          'Le lien n’a pas pu être refait.',
        )
      }
      quandCouperLien={(l) => agirSurLesLiens([(j) => couperLeLien(j, otId, l.id)], 'Le lien n’a pas pu être coupé.')}
      copieChauffeur={copieChauffeur}
      quandCreerLienChauffeur={(n) =>
        agirSurLesLiens([(j) => creerLeLienChauffeur(j, otId, n)], 'Le lien chauffeur n’a pas pu être créé.')
      }
      quandCopierLienChauffeur={(adresse) => {
        void navigator.clipboard.writeText(adresse).then(
          () => setCopieChauffeur(true),
          () => setRefusLiens('La copie a été refusée par le navigateur : sélectionnez l’adresse à la main.'),
        );
      }}
      choix={choix}
      quandRecommencer={() => {
        setEnvoi(true);
        void (async () => {
          const j = await jeton();
          if (j === null) {
            setEnvoi(false);
            quandAccesPerdu();
            return;
          }
          const r = await toutRecommencer(j, otId);
          if (r.etat !== 'ok') {
            setEnvoi(false);
            if (r.cause === 'acces') quandAccesPerdu();
            setRefus(r.message ?? 'Rien n’a été remis à zéro.');
            return;
          }
          // TOUT SE RELIT : lettres, liens, exécution.
          traiter(await lireLesLettres(j, otId), 'Les lettres de voiture n’ont pas pu être lues.');
          const l = await lireLesLiens(j, otId);
          if (l.etat === 'ok') poserLesLiens(l.valeur);
          const x = await lireLExecution(j, otId);
          if (x.etat === 'ok') setExecution(x.valeur);
          setEnvoi(false);
        })();
      }}
      etapes={etapes}
      execution={execution}
      pointage={pointage}
    />
  );
}

/**
 * LES LETTRES SIGNÉES DANS L'ONGLET DOCUMENTS — lot 166 (05/11) : elles y
 * apparaissent d'office, dès la signature au chargement, avec « Voir » et
 * « PDF ». AUCUN FICHIER N'EST STOCKÉ : un document de l'onglet est envoyé par
 * le navigateur d'une personne connectée (`apps/api/src/documents.ts`), et le
 * téléphone du chauffeur n'a pas de compte. La lettre est relue de la base,
 * figée à la signature.
 */
export function VueDocumentsECMR(p: {
  readonly lettres: readonly LettreVoiture[];
  readonly execution: ExecutionLue | null;
  readonly ouverte: LettreVoiture | null;
  readonly quandVoir: (l: LettreVoiture | null) => void;
}) {
  const signees = p.lettres.filter((l) => (executionDeLaLettre(p.execution, l)?.chargement?.signe_le ?? null) !== null);
  if (signees.length === 0) return null;
  return (
    <div className="documents-ecmr">
      <table className="table-documents">
        <thead>
          <tr>
            <th>Type</th>
            <th>Lettre de voiture</th>
            <th>État</th>
            <th>Signée le</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {signees.map((l) => {
            const ex = executionDeLaLettre(p.execution, l);
            return (
              <tr key={l.id}>
                <td>
                  <span className="type-document">eCMR</span>
                </td>
                <td>
                  N° {l.numero} · {l.contenu.expediteur.nom ?? '—'} → {l.contenu.destinataire.nom ?? '—'}
                </td>
                <td>{etatDeLaLettre(l, ex)}</td>
                <td className="code">{momentDe(ex?.chargement?.signe_le ?? null)}</td>
                <td className="actions-document">
                  <button className="lien-action" type="button" onClick={() => p.quandVoir(l)}>
                    Voir
                  </button>
                  <button className="lien-action" type="button" onClick={() => ouvrirLePdfDeLettre(l, ex)}>
                    PDF
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {p.ouverte !== null && (
        <ApercuLettre lettre={p.ouverte} ex={executionDeLaLettre(p.execution, p.ouverte)} quandFermer={() => p.quandVoir(null)} />
      )}
    </div>
  );
}

/** Le conteneur : lit les lettres et l'exécution de l'ordre. Silencieux en cas d'échec. */
export function DocumentsECMR({ jeton, otId }: { readonly jeton: () => Promise<string | null>; readonly otId: string }) {
  const [lettres, setLettres] = useState<readonly LettreVoiture[]>([]);
  const [execution, setExecution] = useState<ExecutionLue | null>(null);
  const [ouverte, setOuverte] = useState<LettreVoiture | null>(null);
  useEffect(() => {
    let vivant = true;
    void (async () => {
      const j = await jeton();
      if (j === null) return;
      const [l, x] = await Promise.all([lireLesLettres(j, otId), lireLExecution(j, otId)]);
      if (!vivant) return;
      if (l.etat === 'ok') setLettres(l.valeur.lettres);
      if (x.etat === 'ok') setExecution(x.valeur);
    })();
    return () => {
      vivant = false;
    };
  }, [jeton, otId]);
  return <VueDocumentsECMR lettres={lettres} execution={execution} ouverte={ouverte} quandVoir={setOuverte} />;
}
